import {
  DataImportRun,
  Alert,
  WritebackRun,
  SubscriptionPlanType,
  DashboardSpreadsheet,
  GetDataImports200Response,
  GetWritebacks200Response,
  GetAlerts200Response,
} from "@coeff/api";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from "react-router-dom";
import styled from "styled-components";

import {
  AppThunkDispatch,
  downloadRunHistory,
  getAlertsByFileId,
  getDataImportRunHistory,
  getDataImportsByFileId,
  getSpreadsheets,
  getTeamActivity,
  getWritebacksByFileId,
  getWritebacksRunHistory,
  updateAlert,
} from "../../actions";
import { AppState } from "../../store";
import { ArrowFlightSqlOAuthManageSettings } from "../ArrowFlightSqlOAuthManageSettings";
import { ArrowFlightSqlOAuthSettings } from "../ArrowFlightSqlOAuthSettings";
import { Billing } from "../Billing/Billing";
import { ConfigureSSO } from "../ConfigureSSO";
import { CustomOAuthSettings } from "../CustomOAuthSettings";
import { DatabricksOAuthSettings } from "../DatabricksOAuthSettings";
import { LookerOAuthManageSettings } from "../LookerOAuthManageSettings";
import { LookerOAuthSettings } from "../LookerOAuthSettings";
import { NetSuiteOAuthManageSettings } from "../NetSuiteOAuthManageSettings";
import { NetSuiteOAuthSettings } from "../NetSuiteOAuthSettings";
import { OAuthConfigurations } from "../OAuthConfigurations";
import { Settings } from "../Settings";
import { SnowflakeOAuthManageSettings } from "../SnowflakeOAuthManageSettings";
import { SnowflakeOAuthSettings } from "../SnowflakeOAuthSettings";
import { TableauOAuthManageSettings } from "../TableauOAuthManageSettings";
import { TableauOAuthSettings } from "../TableauOAuthSettings";
import { WorkspaceAdmin } from "../WorkspaceAdmin/WorkspaceAdmin";

import { Sidebar } from "./components";
import { Activity, Spreadsheets } from "./routes";
import { CloudTables } from "./routes/CloudTables";
import { InviteCoworkers } from "./routes/InviteCoworkers";
import { WebDashboards } from "./routes/WebDashboards";

const Layout = styled.div`
  display: flex;
  height: 100%;
`;

const Content = styled.div`
  display: inline-block;
  width: 100%;
  height: 100%;
  margin-left: 240px;
  min-width: 0;
`;

interface DContext {
  fetchingSheets: boolean;
  planType?: SubscriptionPlanType;
  getAlerts: (targetGoogleSheetsFileId: string) => Promise<GetAlerts200Response>;
  getDataImports: (targetGoogleSheetsFileId: string) => Promise<GetDataImports200Response>;
  getSpreadsheets: () => void;
  getWritebacks: (targetGoogleSheetsFileId: string) => Promise<GetWritebacks200Response>;
  downloadRunHistory: (writebackId: string) => void;
  getWritebacksRunHistory: (
    writebackId: string,
    offset: number
  ) => Promise<{
    writeback_runs: WritebackRun[];
  }>;
  getTeamActivity: () => void;
  getDataImportRunHistory: (
    dataImportId: string,
    offset: number
  ) => Promise<{ data_import_runs: DataImportRun[] }>;
  spreadsheets: DashboardSpreadsheet[];
  coeffUserEmail?: string;
  updateAlert: (alert: Alert) => void;
  writebackDisabledForUser?: boolean;
  isGsuiteDomainAdmin?: boolean;
}

type ConnectDispatchProps = {
  getAlerts: (targetGoogleSheetsFileId: string) => Promise<GetAlerts200Response>;
  getDataImports: (targetGoogleSheetsFileId: string) => Promise<GetDataImports200Response>;
  getSpreadsheets: () => Promise<{
    spreadsheets: Array<DashboardSpreadsheet>;
    total_rows_imported: number;
  }>;
  getWritebacks: (targetGoogleSheetsFileId: string) => Promise<GetWritebacks200Response>;
  downloadRunHistory: (writebackId: string) => void;
  getWritebacksRunHistory: (
    writebackId: string,
    offset: number
  ) => Promise<{
    writeback_runs: WritebackRun[];
  }>;
  getTeamActivity: () => void;
  getDataImportRunHistory: (
    dataImportId: string,
    offset: number
  ) => Promise<{ data_import_runs: DataImportRun[] }>;
  updateAlert: (alert: Alert) => void;
};

type ConnectProps = {
  planType?: SubscriptionPlanType;
  coeffUserEmail?: string;
  writebackDisabledForUser?: boolean;
  isGsuiteDomainAdmin?: boolean;
};

type Props = ConnectProps & ConnectDispatchProps & RouteComponentProps;

type State = {
  fetchingSheets: boolean;
  spreadsheets: Array<DashboardSpreadsheet>;
};

const DashboardContext = React.createContext<DContext | null>(null);

export const useDashboardContext = () => useContext(DashboardContext) as DContext;

class Dashboard extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      fetchingSheets: true,
      spreadsheets: [],
    };
  }

  componentDidMount = () => {
    this.init();
  };

  init = async () => {
    try {
      const { spreadsheets = [] } = await this.props.getSpreadsheets();

      this.setState({
        spreadsheets,
        fetchingSheets: false,
      });
    } catch (error) {
      console.error(error);
      this.setState({
        fetchingSheets: false,
      });
    }
  };

  render = () => {
    const path = this.props.match.path;

    return (
      <DashboardContext.Provider
        value={{
          ...this.state,
          ...this.props,
          planType: this.props.planType,
          fetchingSheets: this.state.fetchingSheets,
          getAlerts: this.props.getAlerts,
          getDataImports: this.props.getDataImports,
          getTeamActivity: this.props.getTeamActivity,
          getSpreadsheets: this.props.getSpreadsheets,
          getWritebacks: this.props.getWritebacks,
          getDataImportRunHistory: this.props.getDataImportRunHistory,
          getWritebacksRunHistory: this.props.getWritebacksRunHistory,
          coeffUserEmail: this.props.coeffUserEmail,
          downloadRunHistory: this.props.downloadRunHistory,
          updateAlert: this.props.updateAlert,
          writebackDisabledForUser: this.props.writebackDisabledForUser,
          isGsuiteDomainAdmin: this.props.isGsuiteDomainAdmin,
        }}
      >
        <Layout>
          <Sidebar />

          <Content>
            <Switch>
              <Route exact path={path}>
                <Redirect to={`${path}/spreadsheets`} />
              </Route>
              <Route path={`${path}/spreadsheets`} component={Spreadsheets} />
              <Route exact path={`${path}/web-dashboards`} component={WebDashboards} />
              <Route exact path={`${path}/datasets`} component={CloudTables} />
              <Route path={`${path}/activity`} component={Activity} />
              <Route path={`${path}/admin`} component={WorkspaceAdmin} />
              <Route path={`${path}/billing`} component={Billing} />
              <Route path={`${path}/settings/oauth-settings`} component={OAuthConfigurations} />
              <Route
                path={`${path}/settings/tableau-oauth/:clientId`}
                component={TableauOAuthManageSettings}
              />
              <Route path={`${path}/settings/tableau-oauth`} component={TableauOAuthSettings} />
              <Route
                path={`${path}/settings/databricks-oauth`}
                component={DatabricksOAuthSettings}
              />
              <Route
                path={`${path}/settings/arrow-flight-sql-oauth/:clientId`}
                component={ArrowFlightSqlOAuthManageSettings}
              />
              <Route
                path={`${path}/settings/arrow-flight-sql-oauth`}
                component={ArrowFlightSqlOAuthSettings}
              />
              <Route path={`${path}/settings/looker-oauth`} component={LookerOAuthSettings} />
              <Route
                path={`${path}/settings/manage-looker-oauth`}
                component={LookerOAuthManageSettings}
              />
              <Route
                path={`${path}/settings/snowflake-oauth/:accountId`}
                component={SnowflakeOAuthManageSettings}
              />
              <Route path={`${path}/settings/snowflake-oauth`} component={SnowflakeOAuthSettings} />
              <Route
                path={`${path}/settings/netsuite-oauth/:accountId`}
                component={NetSuiteOAuthManageSettings}
              />
              <Route path={`${path}/settings/netsuite-oauth`} component={NetSuiteOAuthSettings} />
              {/* Generic per-domain OAuth app settings, one page for every connector in
                  spec.CUSTOM_OAUTH_DATA_SOURCE_TYPES, reached at e.g.
                  `netflix-quiver-oauth` so the URL matches the fixed pages above.

                  MUST stay below every fixed `*-oauth` route: the slug pattern matches
                  those too, and Switch renders the first match. A new fixed OAuth route
                  belongs above this one. */}
              <Route
                path={`${path}/settings/:dataSourceSlug([a-z0-9-]+)-oauth/:clientId`}
                component={CustomOAuthSettings}
              />
              <Route
                path={`${path}/settings/:dataSourceSlug([a-z0-9-]+)-oauth`}
                component={CustomOAuthSettings}
              />
              <Route path={`${path}/settings/configure-sso`} component={ConfigureSSO} />
              <Route path={`${path}/settings`} component={Settings} />
              <Route path={`${path}/invite-coworkers/:eventFrom`} component={InviteCoworkers} />
            </Switch>
          </Content>
        </Layout>
      </DashboardContext.Provider>
    );
  };
}

const mapDispatchToProps = (dispatch: AppThunkDispatch): ConnectDispatchProps => ({
  getAlerts: (targetGoogleSheetsFileId: string) =>
    dispatch(getAlertsByFileId(targetGoogleSheetsFileId)),
  getDataImports: (targetGoogleSheetsFileId: string) =>
    dispatch(getDataImportsByFileId(targetGoogleSheetsFileId)),
  getSpreadsheets: () => dispatch(getSpreadsheets()),
  getTeamActivity: () => dispatch(getTeamActivity()),
  getWritebacks: (targetGoogleSheetsFileId: string) =>
    dispatch(getWritebacksByFileId(targetGoogleSheetsFileId)),
  getDataImportRunHistory: (dataImportId: string, offset: number) =>
    dispatch(getDataImportRunHistory(dataImportId, offset)),
  getWritebacksRunHistory: (writebackId: string, offset: number) =>
    dispatch(getWritebacksRunHistory(writebackId, offset)),
  downloadRunHistory: (writebackId: string) => dispatch(downloadRunHistory(writebackId)),
  updateAlert: (alert: Alert) => dispatch(updateAlert(alert)),
});

const mapStateToProps: (state: AppState) => ConnectProps = ({
  app: { coeffUserEmail, coeffUserId, plan_type, writeback_disabled_for_user, isGsuiteDomainAdmin },
}) => ({
  coeffUserEmail,
  coeffUserId,
  planType: plan_type,
  writebackDisabledForUser: writeback_disabled_for_user,
  isGsuiteDomainAdmin: isGsuiteDomainAdmin,
});

const route = withRouter(connect(mapStateToProps, mapDispatchToProps)(Dashboard));

export { route as Dashboard };
